import Paper from "@mui/material/Paper";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Chip,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Grid from "@mui/material/Grid2";
import { Stack } from "@mui/system";
import CancelIcon from "@mui/icons-material/Cancel";
import { useSelector } from "react-redux";
import { LOOKUP_FIELD_OPTIONS } from "../../../../../constants/lookupFieldOptionsDC";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import {
  setIsLookupFieldsValid,
  setIsPreviewEmpty,
  setLookupFieldDataForPayload,
} from "../../../../../features/uniqueDefSlice";
import PreviewPanel from "../helpers/components/PreviewPanel";
import { buildLookupPayload } from "../helpers/functions/payloadBuilder";

const EditDefinitionLookupFields = forwardRef((_, ref) => {
  const { initialEditData, destinationTablesData } = useSelector(
    (state: any) => state.uniqueDefinition,
  );
  const dispatch = useDispatch();

  const destinationTableName = initialEditData?.destination_table_name || "";

  const destTableID =
    destinationTablesData?.find(
      (item: any) => item.destination_table === destinationTableName,
    )?.id || "";
  const termsList = useMemo(() => {
    return LOOKUP_FIELD_OPTIONS[destTableID] ?? [];
  }, [destTableID]);
  const [selectedTerms, setSelectedTerms] = useState<any[]>([]);
  const [inputValues, setInputValues] = useState({}) as any;
  const [previewValues, setPreviewValues] = useState({});

  const [isInitialized, setIsInitialized] = useState(false);
  useEffect(() => {
    dispatch(setIsPreviewEmpty(Object.keys(previewValues).length === 0));
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewValues]);

  const handleInputChange = (fieldKey: string, value: string) => {
    setInputValues((prev: any) => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  const updateReduxState = useCallback(
    (updatedInputValues: any) => {
      if (Object.keys(updatedInputValues).length === 0) {
        // If no input values, clear the Redux state
        dispatch(setLookupFieldDataForPayload(""));
        return;
      }

      // Construct and format the payload
      const jsonPayload: any = {};
      for (const key in updatedInputValues) {
        if (updatedInputValues[key]) {
          // Only include non-empty values
          jsonPayload[key] = { lookup_field: updatedInputValues[key] };
        }
      }

      if (Object.keys(jsonPayload).length === 0) {
        dispatch(setLookupFieldDataForPayload(""));
      } else {
        const formattedPayload = JSON.stringify(jsonPayload).replace(/"/g, '"');
        dispatch(
          setLookupFieldDataForPayload(`{${formattedPayload.slice(1, -1)}}`),
        );
      }
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleRefreshPreview = () => {
    setPreviewValues({ ...inputValues });
    // Update Redux state when refresh is clicked
    updateReduxState(inputValues);
    dispatch(setIsPreviewEmpty(Object.keys(inputValues).length === 0));
  };

  // Initialize component with initialDuplicateData
  useEffect(() => {
    if (initialEditData && !isInitialized && termsList.length > 0) {
      try {
        const uniqueDefinition = JSON.parse(initialEditData.unique_definition);

        // Extract field names and their lookup values
        const initialInputValues: any = {};
        const initialSelectedTerms: any[] = [];

        Object.entries(uniqueDefinition).forEach(
          ([fieldName, fieldData]: [string, any]) => {
            if (fieldData.lookup_field) {
              // Find the term in termsList that matches this field
              const matchingTerm = termsList.find(
                (term: any) => term.title === fieldName,
              );

              if (matchingTerm) {
                initialSelectedTerms.push(matchingTerm);
                initialInputValues[fieldName] = fieldData.lookup_field;
              }
            }
          },
        );

        if (initialSelectedTerms.length > 0) {
          // Use setTimeout to ensure state updates happen after render
          setTimeout(() => {
            setSelectedTerms([...initialSelectedTerms]);
            setInputValues({ ...initialInputValues });
            setPreviewValues({ ...initialInputValues });

            // Update Redux state with initial data
            updateReduxState(initialInputValues);

            setIsInitialized(true);
          }, 100); // Increased timeout slightly
        } else {
          setIsInitialized(true); // Mark as initialized even if no terms found
        }
      } catch (error) {
        console.error("Error parsing initial duplicate data:", error);
        setIsInitialized(true); // Mark as initialized to prevent retry
      }
    }
  }, [
    initialEditData,
    termsList.length,
    isInitialized,
    destinationTableName,
    termsList,
    updateReduxState,
  ]);

  useEffect(() => {
    // whenever the destination table name changes, clear selections and reset initialization
    setSelectedTerms([]);
    setInputValues({});
    setPreviewValues({});
    setIsInitialized(false);
    dispatch(setIsPreviewEmpty(true));
    dispatch(setLookupFieldDataForPayload("")); // clear Redux payload
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinationTableName]);

  const previewJsonString =
    JSON.stringify(
      Object.fromEntries(
        Object.entries(previewValues).map(([key, val]) => [
          key,
          { lookup_field: val },
        ]),
      ),
      null,
      2,
    ) || "{ Preview Demo }";

  const computeLookupValidity = (
    selectedTerms: any[],
    inputValues: Record<string, string>,
  ) => {
    if (selectedTerms.length === 0) return false;

    return selectedTerms.every(
      (term) =>
        typeof inputValues[term.title] === "string" &&
        inputValues[term.title].trim() !== "",
    );
  };

  useEffect(() => {
    const isValid = computeLookupValidity(selectedTerms, inputValues);
    dispatch(setIsLookupFieldsValid(isValid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTerms, inputValues]);

  useImperativeHandle(ref, () => ({
    refresh: () => {
      const payload = buildLookupPayload(inputValues);

      dispatch(setLookupFieldDataForPayload(payload ?? ""));
      dispatch(setIsPreviewEmpty(!payload));

      return payload;
    },
  }));

  return (
    <>
      <Paper sx={{ mb: 1, pb: 2 }}>
        <Box
          sx={{ paddingTop: "8px", paddingLeft: "16px", paddingRight: "16px" }}
        >
          <Grid
            container
            alignItems={"flex-start"}
            justifyContent={"flex-start"}
            //  display={"flex"}
          >
            <Grid size={12}>
              <Stack width={"100%"}>
                <Typography variant="h6" sx={{ fontSize: "20px", mb: 3 }}>
                  Lookup Fields (Group By)
                </Typography>
                <Autocomplete
                  data-tracker="300"
                  multiple
                  size="small"
                  fullWidth
                  options={termsList}
                  getOptionLabel={(option) => option.title}
                  isOptionEqualToValue={(option, value) =>
                    option.title === value.title
                  }
                  filterSelectedOptions
                  value={selectedTerms}
                  onChange={(_, newValue) => {
                    // Determine removed terms
                    const removed = selectedTerms.filter(
                      (term) =>
                        !newValue.some(
                          (newTerm) => newTerm.title === term.title,
                        ),
                    );

                    // Remove from inputValues and update Redux immediately
                    const updatedInputValues = { ...inputValues };
                    removed.forEach((term) => {
                      delete updatedInputValues[term.title];
                    });

                    setSelectedTerms(newValue);
                    setInputValues(updatedInputValues);

                    // Update Redux state immediately when chips are removed
                    updateReduxState(updatedInputValues);

                    // Also update preview values to reflect the change
                    const updatedPreviewValues: any = { ...previewValues };
                    removed.forEach((term) => {
                      delete updatedPreviewValues[term.title];
                    });
                    setPreviewValues(updatedPreviewValues);
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option: any, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.title}
                        label={option.title}
                        sx={{
                          backgroundColor: "#7B1FA2",
                          color: "#ffffff",
                          height: "27px",
                        }}
                        deleteIcon={
                          <CancelIcon
                            sx={{
                              color: "rgba(255, 255, 255, 0.8) !important",
                              fontSize: "16px !important",
                            }}
                          />
                        }
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search field names or choose from the list"
                      label="Terms In Table"
                      color="secondary"
                      sx={{ mb: 2 }}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                    />
                  )}
                />
                <Grid container spacing={2}>
                  {selectedTerms?.map((term) => (
                    <Grid size={4} key={term.title}>
                      <TextField
                        key={term.title}
                        fullWidth
                        label={term.title}
                        placeholder={`Enter value for ${term.title}`}
                        color="secondary"
                        size="small"
                        sx={{ mb: 1 }}
                        value={inputValues[term.title] || ""}
                        onChange={(e) =>
                          handleInputChange(term.title, e.target.value)
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            </Grid>
            <Grid size={12}>
              <Accordion>
                <AccordionSummary
                  sx={{
                    "& .MuiAccordionSummary-content": {
                      alignItems: "center",
                      margin: 0,
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ExpandMoreIcon />
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "normal", fontSize: 15 }}
                    >
                      Preview
                    </Typography>
                  </Box>
                </AccordionSummary>

                <AccordionDetails
                  sx={{
                    gap: 2,
                    display: "flex",
                    flexDirection: "column",
                    ml: 3,
                  }}
                >
                  <PreviewPanel
                    json={previewJsonString || "{ Preview Demo }"}
                    onRefresh={handleRefreshPreview}
                    // title="Definition - Preview"
                  />
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </>
  );
});

export default EditDefinitionLookupFields;
